import React, { useState, useEffect, useRef } from 'react';
import { QuestionnaireProvider } from './QuestionnaireContext';
import { QuestionnaireStep } from './QuestionnaireStep';
import { QuestionnaireHeader } from './QuestionnaireHeader';
import { QuestionnaireNavigation } from './QuestionnaireNavigation';
import { SupportFooter } from '../SupportFooter';
import { SEO } from '../SEO';
import { steps } from './steps';
import { validateStep } from './validation';
import { logger } from '../../utils/logger';
import { QuestionnaireSessionService } from '../../services/questionnaireSession';
import type { QuestionnaireData } from '../../types';

interface QuestionnaireProps {
  onComplete: (data: QuestionnaireData) => void;
}

export function Questionnaire({ onComplete }: QuestionnaireProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Partial<QuestionnaireData>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const questionnaireRef = useRef<HTMLDivElement>(null);

  const handleNext = () => {
    const stepId = steps[currentStep].id;
    const validation = validateStep(stepId, formData);

    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    if (currentStep === steps.length - 1) {
      const completeData = formData as QuestionnaireData;
      logger.info('Questionnaire completed', { businessType: completeData.businessType });
      onComplete(completeData);
    } else {
      setCurrentStep(prev => prev + 1);
      saveProgress(formData, currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  useEffect(() => {
    if (questionnaireRef.current) {
      questionnaireRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setValidationError(null);
  }, [currentStep]);

  const saveProgress = async (data: Partial<QuestionnaireData>, step: number) => {
    await QuestionnaireSessionService.createOrUpdateSession({
      formData: data,
      currentStep: step,
    });
  };

  const updateFormData = (data: Partial<QuestionnaireData>) => {
    setFormData(prev => ({ ...prev, ...data }));
    setValidationError(null);
  };

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <QuestionnaireProvider>
      <SEO
        title="Find Your Perfect Business Tools - Free Questionnaire | btoolme"
        description="Answer a few quick questions about your business and get personalized software recommendations in under 3 minutes. Find the right tools for your specific needs."
        path="/questionnaire"
        keywords="business software questionnaire, find business tools, software recommendations, tool finder, business tool quiz"
      />
      <div ref={questionnaireRef} className="max-w-3xl mx-auto px-4 sm:px-0">
        <QuestionnaireHeader
          currentStep={currentStep}
          totalSteps={steps.length}
          title={currentStepData.title}
        />

        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
          {validationError && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm sm:text-base text-red-600">{validationError}</p>
            </div>
          )}

          <QuestionnaireStep
            stepId={currentStepData.id}
            data={formData}
            onChange={updateFormData}
          />

          <QuestionnaireNavigation
            currentStep={currentStep}
            totalSteps={steps.length}
            onPrevious={handlePrevious}
            onNext={handleNext}
            isValid={!validationError}
            isLastStep={isLastStep}
          />
        </div>

        <SupportFooter className="mt-8" minimal />
      </div>
    </QuestionnaireProvider>
  );
}

// Default export
export default Questionnaire;